import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { InvoiceCard, SearchView } from "../../../components";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { countryOptions } from "@/utils/country-options";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import {
  getMyPaymentsCommissionDetails,
  downloadMyPaymentsCommissionDetailsExcel,
} from "@/data/requests/dashboard-requests";

const STATUS_OPTIONS = [
  { value: "Approved", label: "Onaylandı" },
  { value: "Change Requested", label: "Değişiklik İstendi" },
  { value: "Troubled", label: "Sorunlu" },
  { value: "Invoice Uploaded", label: "Fatura Yüklendi" },
  { value: "Mail Sended", label: "Mail Gönderildi" },
  { value: "Accounting", label: "Muhasebe" },
  { value: "Auto Created Invoice", label: "Otomatik Oluşturulan Fatura" },
  { value: "OCR Completed", label: "OCR Tamamlandı" },
  { value: "LUCA", label: "LUCA" },
  { value: "InBank", label: "Bankada" },
  { value: "Paid", label: "Ödendi" },
  { value: "PAID-TIMEOUT", label: "ÖDEME-ZAMAN AŞIMI" },
  { value: "Not Paid", label: "Ödenmedi" },
  { value: "Return", label: "İade" },
  { value: "all", label: "Tümü" },
];

export function MyPaymentsCommissionDetailsScreen() {
  const { isAdvancedTable } = useTableView();
  const { toast } = useToast();
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [pageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    name: "",
    email: "",
    lucaId: "",
    managerName: "",
    status: "",
    country: "",
  });

  const navigate = useNavigate();

  const [page, setPage] = useState(() => {
    const storedPage = localStorage.getItem(`myPaymentsCommissionDetailsPage`);
    return storedPage ? parseInt(storedPage, 10) : 1;
  });
  const [queryParams, setQueryParams] = useState(() => {
    const storedQueryParams = localStorage.getItem(
      `myPaymentsCommissionDetailsQueryParams`
    );
    if (storedQueryParams && storedQueryParams !== "{}") {
      setSearchParams(JSON.parse(storedQueryParams));
    }
    return storedQueryParams ? JSON.parse(storedQueryParams) : {};
  });

  useEffect(() => {
    localStorage.setItem(`myPaymentsCommissionDetailsPage`, page.toString());
    localStorage.setItem(
      `myPaymentsCommissionDetailsQueryParams`,
      JSON.stringify(queryParams)
    );
  }, [page, queryParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "my-payments-commission-details",
      page,
      pageSize,
      queryParams,
      sortColumn,
      sortDirection,
    ],
    queryFn: () =>
      getMyPaymentsCommissionDetails({
        page,
        pageSize,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        lucaIdFilter: queryParams.lucaId,
        statusFilter: queryParams.status,
        countryFilter: queryParams.country,
        managerNameFilter: queryParams.managerName,
        sortColumn,
        sortDirection,
      }),
    onError: (error) => {
      toast({
        title: "Komisyon detayları yüklenirken hata oluştu",
        description:
          error.response?.data?.message || "Beklenmeyen bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const downloadExcel = () => {
    downloadMyPaymentsCommissionDetailsExcel({
      nameFilter: queryParams.name,
      emailNameFilter: queryParams.email,
      lucaIdFilter: queryParams.lucaId,
      statusFilter: queryParams.status,
      countryFilter: queryParams.country,
      managerNameFilter: queryParams.managerName,
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "my-commission-details.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd.MM.yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const pageSettings = {
    title: "Ödemelerim - Komisyon Detayları",
    actions: [
      {
        name: "Excel İndir",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        onClick: downloadExcel,
      },
    ],
  };

  // Simple columns for basic table
  const simpleColumns = [
    {
      key: "name",
      label: "Ad",
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "email",
      label: "E-posta",
      accessor: (item) => item.commission?.email || "",
    },
    {
      key: "paymentName",
      label: "Ödeme",
      accessor: (item) => item.commission?.payment?.name || "",
    },
    {
      key: "ourInvoiceId",
      label: "Fatura Tarihimiz",
      accessor: (item) => item.commission?.invoiceId || "",
    },
    {
      key: "invoiceId",
      label: "Fatura No",
      accessor: (item) => item.invoiceId || "",
    },
    {
      key: "bankAccountHolder",
      label: "Hesap Sahibi",
      accessor: (item) => item.bankAccountHolder || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Hesap Adı",
      accessor: (item) => item.commission?.client?.lucaAccountName || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      accessor: (item) => item.commission?.client?.lucaId || "",
    },
    {
      key: "totalAmount",
      label: "Toplam Tutar",
      accessor: (item) => item.totalAmount || "",
    },
    {
      key: "status",
      label: "Durum",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.status}</span>
          {row.status === "Return" && row.returnReason && (
            <div className="relative group">
              <svg
                className="h-4 w-4 text-blue-500 cursor-help"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                Return Reason: {row.returnReason}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
          {row.status === "Paid" && row.paymentDate && (
            <div className="relative group">
              <svg
                className="h-4 w-4 text-blue-500 cursor-help"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                Paid Date: {row.paymentDate}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: "İşlemler",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            onClick={() =>
              navigate(
                `/payments/${row.commission?.paymentId}/invoices/${row.id}`
              )
            }
          >
            Detayları Gör
          </Button>
        </div>
      ),
    },
  ];

  // Advanced columns for advanced table
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    {
      key: "clientName",
      label: "Müşteri Adı",
      sortable: false,
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "influencerName",
      label: "Influencer",
      sortable: false,
      accessor: (item) => item.commission?.influencerName || "",
    },
    {
      key: "commissionEmail",
      label: "Komisyon E-postası",
      sortable: false,
      accessor: (item) => item.commission?.email || "",
    },
    {
      key: "paymentName",
      label: "Ödeme",
      sortable: false,
      accessor: (item) => item.commission?.payment?.name || "",
    },
    { key: "email", label: "Fatura E-postası", sortable: true },
    { key: "phoneNumber", label: "Telefon", sortable: true },
    { key: "recipientName", label: "Alıcı Adı", sortable: true },
    { key: "bankAccountHolder", label: "Hesap Sahibi", sortable: true },
    { key: "iban", label: "IBAN", sortable: false },
    { key: "bic", label: "BIC", sortable: false },
    { key: "bankName", label: "Banka Adı", sortable: true },
    { key: "totalAmount", label: "Toplam Tutar", sortable: true },
    { key: "currency", label: "Para Birimi", sortable: true },
    { key: "invoiceId", label: "Fatura No", sortable: true },
    { key: "invoiceDate", label: "Fatura Tarihi", sortable: true },
    { key: "dueDate", label: "Vade Tarihi", sortable: true },
    { key: "ourCompany", label: "Şirketimiz", sortable: true },
    { key: "status", label: "Durum", sortable: true },
    { key: "paymentDate", label: "Ödeme Tarihi", sortable: true },
    { key: "returnDate", label: "İade Tarihi", sortable: true },
    { key: "returnReason", label: "İade Sebebi", sortable: false },
    {
      key: "commissionInvoiceId",
      label: "Fatura Tarihimiz",
      sortable: false,
      accessor: (item) => item.commission?.invoiceId || "",
    },
    {
      key: "finalAmount",
      label: "Net Tutar",
      sortable: false,
      accessor: (item) => item.commission?.finalAmount || "",
    },
    {
      key: "managerName",
      label: "Menajer",
      sortable: false,
      accessor: (item) => item.commission?.managerName || "",
    },
    {
      key: "country",
      label: "Ülke",
      sortable: false,
      accessor: (item) => item.commission?.country || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      sortable: false,
      accessor: (item) => item.commission?.client?.lucaId || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Hesap Adı",
      sortable: false,
      accessor: (item) => item.commission?.client?.lucaAccountName || "",
    },
    {
      key: "clientCurrency",
      label: "Müşteri Para Birimi",
      sortable: false,
      accessor: (item) => item.commission?.client?.currency || "",
    },
    {
      key: "clientBankName",
      label: "Müşteri Bankası",
      sortable: false,
      accessor: (item) => item.commission?.client?.bankName || "",
    },
    {
      key: "clientIBAN",
      label: "Müşteri IBAN",
      sortable: false,
      accessor: (item) => item.commission?.client?.IBAN || "",
    },
    {
      key: "createdAt",
      label: "Oluşturulma",
      sortable: true,
      accessor: (item) =>
        item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("tr-TR")
          : "",
    },
    {
      key: "actions",
      label: "İşlemler",
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() =>
              navigate(
                `/payments/${row.commission?.paymentId}/invoices/${row.id}`
              )
            }
          >
            Görüntüle
          </Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = [
    "clientName",
    "commissionEmail",
    "paymentName",
    "totalAmount",
    "status",
    "country",
    "lucaId",
    "actions",
  ];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
      style={{ padding: "0 10px" }}
    >
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        countryOptions={countryOptions}
        resetDefault={() => {
          setSearchParams({
            name: "",
            email: "",
            lucaId: "",
            status: "",
            country: "",
            managerName: "",
          });
          setQueryParams({
            name: "",
            email: "",
            lucaId: "",
            status: "",
            country: "",
            managerName: "",
          });
          setPage(1);
        }}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={advancedColumns}
          columnVisibilityKey="my-payments-commission-details-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(
              `/payments/${item.commission?.paymentId}/invoices/${item.id}`
            )
          }
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={simpleColumns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(
              `/payments/${item.commission?.paymentId}/invoices/${item.id}`
            )
          }
          renderGridItem={(item) => (
            <InvoiceCard
              key={item.id}
              name={item.commission?.client?.name}
              email={item.email}
              invoiceId={item.commission?.invoiceId}
              finalAmount={item.commission?.finalAmount}
              status={item.status}
              returnReason={item.returnReason}
              onDetailClick={() =>
                navigate(
                  `/payments/${item.commission?.paymentId}/invoices/${item.id}`
                )
              }
            />
          )}
        />
      )}
    </AppLayout>
  );
}
